import { useClientCategories } from "api/Client/category";
import { useClientProducts } from "api/Client/product";
import { image_host } from "config/api_host";
import { motion } from "framer-motion";
import { useState } from "react";
import { FaHeadphones, FaInfoCircle } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";

import SpotlightCard from "components/home/SpotlightCard";

import laplap from "assets/laplap.png";
import LogoSpinner from "components/Spinner/LogoSpinner";

function Accessories() {
  const { prand } = useParams();

  const {
    data: categories,
    isLoading: categoriesIsLoading,
    isSuccess: categoriesIsSuccess,
    isError: categoriesIsError,
  } = useClientCategories("accessories");

  const {
    data: product,
    isLoading: productIsLoading,
    isSuccess: productIsSuccess,
  } = useClientProducts(prand);

  if (!prand) {
    // الحالة الثانية: يوجد type فقط
    return (
      <div className="max-w-6xl p-4  mx-auto w-full h-fit flex flex-col gap-8 mt-">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-mainLight/10 rounded-2xl text-mainLight shadow-inner">
            <FaHeadphones size={24} />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
            الاكسسوارات
          </h2>{" "}
        </div>
        {categoriesIsLoading && <LogoSpinner />}
        {categoriesIsSuccess && (
          <section className="p-4 py-6 font-[Tajawal] grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 max-w-6xl mx-auto w-full h-fit">
            {categories.data.categories.map((prand) => (
              <LaptopPrand key={prand.id} prand={prand} />
            ))}
          </section>
        )}
      </div>
    );
  }

  if (prand) {
    // الحالة الثالثة: يوجد الاثنان معًا
    return (
      <>
        {productIsLoading && <LogoSpinner />}
        {productIsSuccess && (
          <section className="p-4 py-6 font-[Tajawal] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto w-full">
            {product.data.products.map((lap) => (
              <LaptopCard key={lap.id} laptop={lap} />
            ))}
          </section>
        )}
      </>
    );
  }
}

function LaptopCard({ laptop }) {
  const [flipped, setFlipped] = useState(false);

  const technicalFields = laptop.fields ? laptop.fields : [];
  const isAvailable = laptop.quantity > 0 || laptop.isAvailable === 1;
  const hasDiscount =
    Number(laptop.discountPriceDollar) > 0 &&
    Number(laptop.discountPriceDollar) < Number(laptop.salesPriceDollar);

  return (
    <div
      className="relative w-full h-[450px] [perspective:1500px] group cursor-pointer"
      onClick={() => setFlipped(!flipped)}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, type: "spring", stiffness: 100 }}
        className="relative w-full h-full [transform-style:preserve-3d]"
      >
        {/* --- الوجه الأمامي: العرض التسويقي --- */}
        <div className="absolute inset-0 rounded-[2rem] border border-gray-200 dark:border-white/10 p-4 bg-gray-100 dark:bg-white/5 backdrop-blur-md [backface-visibility:hidden] flex flex-col items-center shadow-xl dark:shadow-2xl transition-colors duration-300">
          {/* شارة التوفر */}
          <div
            className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[10px] font-bold z-10 ${
              isAvailable
                ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"
            }`}
          >
            {isAvailable ? "متوفر في المخزن" : "نفدت الكمية"}
          </div>

          {/* شارة الخصم */}
          {hasDiscount && (
            <div className="absolute top-4 left-4 bg-red-500 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg z-20 animate-bounce">
              خصم{" "}
              {Math.round(
                ((laptop.salesPriceDollar - laptop.discountPriceDollar) /
                  laptop.salesPriceDollar) *
                  100
              )}
              %
            </div>
          )}

          {/* صورة المنتج */}
          <div className="mt-8 relative h-36 flex items-center">
            <div className="absolute inset-0 bg-mainLight/20 dark:bg-mainLight/10 blur-3xl rounded-full opacity-50 dark:opacity-100"></div>
            <img
              src={image_host + laptop.image}
              alt={laptop.name}
              className="relative z-10 w-48 h-full object-contain drop-shadow-xl dark:drop-shadow-[0_15px_30px_rgba(0,0,0,0.4)]"
            />
          </div>

          {/* النصوص */}
          <div className="text-center mt-4 w-full px-2">
            <span className="text-mainLight text-xs font-bold tracking-widest uppercase opacity-80">
              {laptop.companyName}
            </span>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mt-1 truncate">
              {laptop.name}
            </h3>
            {laptop.desc && (
              <p className="text-gray-500 dark:text-gray-400 text-xs mt-2 line-clamp-3 leading-relaxed">
                {laptop.desc}
              </p>
            )}
          </div>

          {/* السعر */}
          <div className="mt-auto w-full flex justify-center items-center gap-3 bg-gray-50 dark:bg-white/5 py-3 rounded-2xl border border-gray-100 dark:border-white/5 transition-colors">
            {hasDiscount ? (
              <>
                <s className="text-gray-400 dark:text-gray-500 font-bold text-sm">
                  ${Number(laptop.salesPriceDollar).toLocaleString()}
                </s>
                <span className="text-mainLight font-bold text-xl">
                  ${Number(laptop.discountPriceDollar).toLocaleString()}
                </span>
              </>
            ) : (
              <span className="text-mainLight font-bold text-xl">
                ${Number(laptop.salesPriceDollar).toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {/* --- الوجه الخلفي: المعلومات الكاملة --- */}
        <div className="absolute inset-0 rounded-[2rem] border border-mainLight/30 p-4 bg-gray-200 dark:bg-mainDark/95 backdrop-blur-2xl [transform:rotateY(180deg)] [backface-visibility:hidden] flex flex-col shadow-2xl transition-colors duration-300">
          <div className="flex justify-between items-start mb-4">
            <div className="text-right">
              <h4 className="text-gray-800 dark:text-white font-bold text-lg">
                {laptop.name}
              </h4>
              <p className="text-mainLight text-xs font-bold uppercase">
                Technical Specs
              </p>
            </div>
            <div className="bg-mainLight/10 dark:bg-mainLight/20 p-2 rounded-lg">
              <FaInfoCircle className="text-mainLight" />
            </div>
          </div>

          {/* المواصفات */}
          <div className="grid grid-cols-2 gap-3 flex-1 overflow-y-auto pt-1 pb-2 custom-scrollbar">
            {technicalFields.map((field, idx) => (
              <div
                key={idx}
                className="bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-1.5 h-12 rounded-xl flex flex-col items-center justify-center text-center transition-colors"
              >
                <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase">
                  {field.name}
                </span>
                <span className="text-gray-700 dark:text-white text-xs font-bold truncate w-full px-1">
                  {field.value}
                </span>
              </div>
            ))}
          </div>

          {/* الكمية */}
          <div className="bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-2 rounded-xl h-12 flex flex-col items-center justify-center text-center mb-4 transition-colors">
            <span className="text-[9px] text-gray-400 dark:text-gray-500 font-bold uppercase">
              الكمية المتاحة
            </span>
            <span
              className={`text-xs font-bold ${
                laptop.quantity < 5
                  ? "text-orange-500"
                  : "text-gray-700 dark:text-white"
              }`}
            >
              {laptop.quantity} قطعة
            </span>
          </div>

          {/* زر الشراء */}
          <div className="mt-auto">
            <Link
              to={`/product/${laptop.id}`}
              onClick={(e) => e.stopPropagation()}
              className="block w-full py-3 rounded-xl bg-mainLight text-mainDark font-bold text-center text-sm shadow-lg hover:scale-[1.02] active:scale-95 transition-all"
            >
              المزيد من التفاصيل
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function Tags({ data }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-sm">
      {data.map((t) => (
        <Tag label={t.name} value={t.value || " - - "} />
      ))}
    </div>
  );
}

function Tag({ label, value }) {
  return (
    <div className="flex flex-col border border-gray-300/30 dark:border-gray-600 rounded-lg p-2 text-center">
      <span className="text-[11px] uppercase text-gray-400">{label}</span>
      <span className="font-medium text-sm truncate">{value}</span>
    </div>
  );
}

function LaptopPrand({ prand }) {
  return (
    <motion.div whileHover={{ y: -10 }} className="relative group h-fit">
      <Link to={`${prand.id}`} className="flex flex-col items-center gap-4">
        <div className="relative w-full aspect-square flex items-center justify-center p-8 rounded-[2.5rem] bg-white/5 border border-white/10 backdrop-blur-xl shadow-2xl_ group-hover:border-mainLight/40 group-hover:shadow-mainLight/10 transition-all duration-500">
          {/* تأثير الضوء خلف الشعار */}
          <div className="absolute inset-0 bg-mainLight/5 opacity-0 group-hover:opacity-100 rounded-[2.5rem] blur-2xl transition-opacity"></div>

          <img
            src={image_host + prand.image}
            alt={prand.name}
            className="w-full h-full object-contain filter   transition-all duration-500 drop-shadow-lg"
          />
        </div>
        <h3 className="text-xl font-bold text-gray-300 group-hover:text-mainLight transition-colors">
          {prand.name}
        </h3>
      </Link>
    </motion.div>
  );
}

export default Accessories;
